
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useDuplicateCheck } from "@/hooks/resume/useDuplicateCheck";

/**
 * Hook for managing resume upload state and operations
 */
export const useResumeUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [resumeContent, setResumeContent] = useState<string>('');
  const [originalFilename, setOriginalFilename] = useState<string>('');
  const [uploadComplete, setUploadComplete] = useState(false);
  const [isDuplicateResume, setIsDuplicateResume] = useState(false);
  const { toast } = useToast();
  const { checkDuplicateResume } = useDuplicateCheck();

  // Handle file upload completion
  const handleUploadComplete = async (content: string, filename: string) => {
    setResumeContent(content);
    setOriginalFilename(filename);
    setUploadComplete(true);
    setIsUploading(false);
    
    // Check if this resume was already uploaded before
    const isDuplicate = await checkDuplicateResume(filename);
    setIsDuplicateResume(isDuplicate);
    
    if (isDuplicate) {
      toast({
        title: "Duplicate Resume",
        description: "You've already uploaded this resume before. You can continue to optimize it for a different job.",
        variant: "default"
      });
    }
  };

  const resetUpload = () => {
    setUploadComplete(false);
    setResumeContent('');
    setOriginalFilename('');
    setIsDuplicateResume(false);
  };

  return {
    isUploading,
    setIsUploading,
    resumeContent,
    originalFilename,
    uploadComplete,
    isDuplicateResume,
    handleUploadComplete,
    resetUpload
  };
};
